import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { Database, FileText, LayoutGrid, RotateCcw, Play } from "lucide-react";
import { toast } from "sonner";
import { WidgetCard, widgetSpan } from "../components/widgets/WidgetCard";
import TourBanner from "../components/TourBanner";
import TourCarousel from "../components/TourCarousel";
import { SyntheticBanner } from "../components/BaselineSeeding";
import BaselineReadinessCard from "../components/BaselineReadinessCard";
import MultiLocaleFxCard from "../components/MultiLocaleFxCard";

const DEFAULT_YEAR = new Date().getFullYear();

export default function Dashboard() {
  const { user, tenant } = useAuth();
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(DEFAULT_YEAR);
  const [tourSeen, setTourSeen] = useState(true);
  const [tourModules, setTourModules] = useState([]);
  const [tourOpen, setTourOpen] = useState(false);

  const load = (y = year) => api.get(`/widgets/catalog?year=${y}`).then((r) => {
    setCat(r.data);
    if (r.data.years?.length && !r.data.years.includes(y)) setYear(r.data.years[r.data.years.length - 1]);
  }).finally(() => setLoading(false));
  useEffect(() => { load(year); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || user.role === "super_admin") return;
    api.get("/training/tour-status").then((r) => setTourSeen(r.data.seen)).catch(() => {});
    api.get("/training/walkthroughs").then((r) => setTourModules(r.data.modules || [])).catch(() => {});
  }, [user]);

  const markTourSeen = () => { api.post("/training/tour-complete").catch(() => {}); setTourSeen(true); };
  const finishTour = () => { setTourOpen(false); markTourSeen(); };

  const hasTour = tourModules.some((m) => m.has_media);
  const showBanner = !tourSeen && user && user.role !== "super_admin" && hasTour;

  const saveLayout = async (enabled) => {
    try { await api.put("/widgets/layout", { enabled }); load(); }
    catch (e) { toast.error("Failed to update layout"); }
  };
  const removeWidget = (key) => {
    if (!cat) return;
    saveLayout(cat.enabled.filter((k) => k !== key));
    toast.success("Widget removed");
  };
  const resetLayout = async () => {
    try { await api.post("/widgets/layout/reset"); toast.success("Reset to recommended layout"); load(); }
    catch (e) { toast.error("Failed to reset"); }
  };

  if (loading) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading control tower…</div>;

  const widgets = cat?.enabled_widgets || [];
  const empty = !widgets.length;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="dashboard-page">
      <SyntheticBanner />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-1">Control Tower · FY{year}</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter">
            {tenant ? `Welcome, ${user?.name?.split(" ")[0]}` : "Super Admin View"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            Your balanced ESG posture across Environmental, Social &amp; Governance — tailored to your SASB industry{tenant?.industry_name ? `: ${tenant.industry_name}` : ""}. Add or remove widgets to make it yours.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {(cat?.years?.length > 1) && (
            <div className="inline-flex rounded-sm border border-border overflow-hidden mr-1" data-testid="year-switcher">
              {cat.years.map((y) => (
                <button key={y} onClick={() => setYear(y)} data-testid={`year-switch-${y}`}
                  className={`px-3 py-1.5 text-xs font-mono transition-colors ${y === year
                    ? "bg-accent text-accent-foreground font-bold"
                    : "text-muted-foreground hover:text-foreground hover:bg-surface-hover"}`}>
                  FY{y}
                </button>
              ))}
            </div>
          )}
          {hasTour && user?.role !== "super_admin" && <Button variant="ghost" size="sm" onClick={() => setTourOpen(true)} data-testid="open-tour-btn"><Play className="h-3.5 w-3.5" /> Tour</Button>}
          <Button variant="ghost" size="sm" onClick={resetLayout} data-testid="reset-layout-btn"><RotateCcw className="h-3.5 w-3.5" /> Reset</Button>
          <Link to="/custom-kpis?tab=library"><Button variant="outline" size="sm" data-testid="add-widgets-btn"><LayoutGrid className="h-3.5 w-3.5" /> Widget Library</Button></Link>
          <Link to="/data"><Button variant="outline" size="sm" data-testid="quick-ingest-btn"><Database className="h-3.5 w-3.5" /> Ingest</Button></Link>
          <Link to="/reports"><Button variant="primary" size="sm" data-testid="quick-report-btn"><FileText className="h-3.5 w-3.5" /> Generate Report</Button></Link>
        </div>
      </div>

      {showBanner && <TourBanner onStart={() => setTourOpen(true)} onDismiss={markTourSeen} />}

      {tenant && user?.role !== "super_admin" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="insight-cards-row">
          <BaselineReadinessCard />
          <MultiLocaleFxCard year={year} />
        </div>
      )}

      {empty ? (
        <div className="card-flat p-12 text-center" data-testid="dashboard-empty">
          <LayoutGrid className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <h2 className="font-display text-2xl font-bold">Your control tower is empty.</h2>
          <p className="text-muted-foreground mt-2">Add widgets from the library or reset to the recommended balanced layout.</p>
          <div className="flex gap-2 justify-center mt-6">
            <Link to="/custom-kpis?tab=library"><Button variant="primary" data-testid="empty-add-widgets">Open Widget Library</Button></Link>
            <Button variant="outline" onClick={resetLayout}>Use recommended layout</Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-min" data-testid="widget-grid">
          {widgets.map((w) => (
            <div key={w.key} className={widgetSpan(w.render)}>
              <WidgetCard w={w} onRemove={removeWidget} provYear={year} />
            </div>
          ))}
        </div>
      )}

      <TourCarousel open={tourOpen} modules={tourModules} onClose={() => setTourOpen(false)} onComplete={finishTour} />
    </div>
  );
}
